/**
 * The only unauthenticated screen.
 *
 * One button and no form, because there is no password to type — Entra owns the
 * credential and this app never sees one. The screen's whole job is telling
 * someone what this is before they click.
 *
 * Where they were heading is remembered in sessionStorage rather than round
 * -tripped through the provider: the `auth` block always returns the browser to
 * one configured URL, and adding a `next` parameter to the OIDC flow would mean
 * bending the block for something the frontend can hold on its own.
 */

import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Logo } from "@/components/logo";
import { goToPath } from "@/lib/project-url";
import { useAuthStatus, signIn, takeReturnTo } from "./hooks";
import { MicrosoftMark } from "./microsoft-mark";
import { ThemeSelector } from "@/components/theme-selector";

export function LoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStatus();
  const next = params.get("next") ?? "/";

  // Someone who is already signed in landing on /login (a stale bookmark, the
  // back button after logging in) should not be shown a sign-in button.
  useEffect(() => {
    if (isAuthenticated) {
      const remembered = takeReturnTo();
      const target = next.startsWith("/") && !next.startsWith("//") ? next : "/";
      goToPath(target !== "/" ? target : remembered, navigate);
    }
  }, [isAuthenticated, navigate, next]);

  return (
    <div className="grid min-h-screen place-items-center bg-subtle px-6">
      <main className="flex w-full max-w-[400px] flex-col gap-6">
        <div className="flex items-center gap-2.5">
          <Logo size={30} />
          <h1 className="text-lg font-semibold">Agentic Workbench</h1>
        </div>
        <section className="box flex flex-col gap-5 p-7">
          <div className="flex flex-col gap-1.5">
            <h2 className="text-[17px] font-semibold">Sign in</h2>
            <p className="text-[13px] leading-relaxed text-muted">
              Test your agents on datasets, score every answer, and see what changed between experiments.
            </p>
          </div>
          <button type="button" onClick={() => signIn(next)} className="btn h-10 w-full gap-3 text-sm">
            <MicrosoftMark size={16} />
            Continue with Microsoft
          </button>
        </section>
        <div className="mx-auto w-[220px]">
          <ThemeSelector />
        </div>
        <p className="text-center text-xs text-faint">Qure AI · use your work account</p>
      </main>
    </div>
  );
}
